import React, { useMemo } from 'react';
import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import { CommentInput as BaseCommentInput } from '@producer/ui-kit';

import { EditorBubbleMenu } from '../rich-editor/editor-bubble-menu';
import { MentionList, MentionListRef } from './mention-list';
import { context } from '../../app/context';

import type {
  CommentInputProps as BaseCommentInputProps,
  MentionSuggestion,
} from '@producer/ui-kit';

type CommentInputProps = Omit<BaseCommentInputProps, 'mentionSuggestion' | 'renderBubbleMenu'>;

/**
 * The shared comment editor with what only the webapp can supply: the space members to mention
 * and the formatting toolbar.
 */
export const CommentInput: React.FC<CommentInputProps> = (props) => {
  const space = context.space;

  // Get all space members for mention suggestions
  const mentionItems: MentionSuggestion[] = useMemo(() => {
    return space.members.map((member) => ({
      id: member._id,
      label: member.fullName,
      avatar: member.avatar,
      initials: member.initials,
    }));
  }, [space.members]);

  // Configure mention suggestion
  const mentionSuggestion = useMemo(
    () => ({
      items: ({ query }: { query: string }) => {
        return mentionItems
          .filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))
          .slice(0, 5);
      },
      render: () => {
        let component: ReactRenderer<MentionListRef>;
        let popup: TippyInstance[];

        return {
          onStart: (props: any) => {
            component = new ReactRenderer(MentionList, {
              props,
              editor: props.editor,
            });

            if (!props.clientRect) {
              return;
            }

            popup = tippy('body', {
              getReferenceClientRect: props.clientRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
            });
          },

          onUpdate(props: any) {
            component.updateProps(props);

            if (!props.clientRect) {
              return;
            }

            popup[0].setProps({
              getReferenceClientRect: props.clientRect,
            });
          },

          onKeyDown(props: any) {
            if (props.event.key === 'Escape') {
              popup[0].hide();

              return true;
            }

            return component.ref?.onKeyDown(props) || false;
          },

          onExit() {
            popup[0].destroy();
            component.destroy();
          },
        };
      },
    }),
    [mentionItems],
  );

  return (
    <BaseCommentInput
      {...props}
      mentionSuggestion={mentionSuggestion}
      renderBubbleMenu={(editor) => <EditorBubbleMenu editor={editor} />}
    />
  );
};
